import { useStations } from '@app/core/stations/hooks/useStations';
import RadioIcon from '@mui/icons-material/Radio';
import TvIcon from '@mui/icons-material/Tv';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getStationTypeFilterType } from '../../helpers/get-station-type-filter-type';
import { useStatusQueryFilter } from '../../hooks/useStatusQueryFilter';
import { StationTypeFilterType } from '../options';

const ALL_STATION_TYPE_FILTERS = [
  StationTypeFilterType.RADIO,
  StationTypeFilterType.TV,
  StationTypeFilterType.YOUTUBE,
] as const;

/**
 * Non-exclusive toggle group for the station types the user actually has.
 * No type param = show all (all available types selected in UI).
 * Renders nothing when the user has only one station type among their stations.
 */
export function StatusStationTypeFilter() {
  const { $t } = useIntl();
  const stations = useStations();
  const { queryParams, setQueryParams } = useStatusQueryFilter();

  const availableStationTypes = useMemo((): StationTypeFilterType[] => {
    const types = new Set(stations.map(getStationTypeFilterType));

    return ALL_STATION_TYPE_FILTERS.filter((type) => types.has(type));
  }, [stations]);

  const selectedTypes = useMemo(() => {
    if (queryParams.type == null) {
      return availableStationTypes;
    }

    const filteredSelection = queryParams.type.filter((type) =>
      availableStationTypes.includes(type),
    );

    return filteredSelection.length > 0 ? filteredSelection : availableStationTypes;
  }, [queryParams.type, availableStationTypes]);

  if (availableStationTypes.length <= 1) {
    return null;
  }

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: StationTypeFilterType[],
  ) => {
    if (newValue.length === 0 || newValue.length === availableStationTypes.length) {
      setQueryParams({ type: null });
    } else {
      setQueryParams({ type: newValue });
    }
  };

  return (
    <ToggleButtonGroup
      value={selectedTypes}
      onChange={handleChange}
      aria-label={$t({
        id: 'logger.statusScreen.stationType',
        defaultMessage: 'Station type',
      })}
      size="small"
    >
      {availableStationTypes.includes(StationTypeFilterType.RADIO) && (
        <ToggleButton
          value={StationTypeFilterType.RADIO}
          aria-label={$t({
            id: 'logger.statusScreen.stationTypeRadio',
            defaultMessage: 'Radio',
          })}
          color="info"
        >
          <RadioIcon fontSize="small" />
        </ToggleButton>
      )}
      {availableStationTypes.includes(StationTypeFilterType.TV) && (
        <ToggleButton
          value={StationTypeFilterType.TV}
          aria-label={$t({
            id: 'logger.statusScreen.stationTypeTv',
            defaultMessage: 'TV',
          })}
          color="info"
        >
          <TvIcon fontSize="small" />
        </ToggleButton>
      )}
      {availableStationTypes.includes(StationTypeFilterType.YOUTUBE) && (
        <ToggleButton
          value={StationTypeFilterType.YOUTUBE}
          aria-label={$t({
            id: 'logger.statusScreen.stationTypeYoutube',
            defaultMessage: 'YouTube',
          })}
          color="info"
        >
          <YouTubeIcon fontSize="small" />
        </ToggleButton>
      )}
    </ToggleButtonGroup>
  );
}
